import { Component, ViewEncapsulation, Input, ViewChild, AfterViewInit, Output, EventEmitter, TemplateRef, ViewContainerRef, HostListener } from '@angular/core';
import { TimeoutQueue } from '@common/services/timeout-queue';
import { AppService } from '@suite/app.service';
import { PlaybookService } from '@playbook/services/playbook-service';
import { LayoutState } from "@common/services/layout-state";
import { TopicRecord } from '@src/suite/playbook/data-models/records/topic-record';
import { Subscription } from 'rxjs';
import { BasicContentService } from '@common/services/basic-content-service';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { MatButton } from '@angular/material/button';
import { EnpsRecordControl } from '@common/controls/enps-record-control/enps-record-control';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { SingleFeedViewStateRecord } from '@common/data-models/records/single-feed-viewstate-record';
import { TopicViewStateRecord } from '@common/data-models/records/topic-viewstate-record';
import { FieldDef } from '@common/data-models/service/field-def';
import { DraggableEnpsComponent } from '@common/components/base/draggable-enps-component';
import { FieldDefValidations } from '@common/enums/enums';
import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import { VisualStateService } from '@common/services/visual-state-service';
import { MultiValue } from '../../data-models/complex-types/multi-value';
import { DateTime } from '../../data-models/complex-types/date-time';
import { Staff } from '../../data-models/service/staff';
import { AssignmentRecord } from '@src/suite/playbook/data-models/records/assignment-record';
import { LanguageSettings } from '../../services/language-settings';
import { PublicationViewStateRecord } from '../../data-models/records/publication-viewstate-record';


@Component({
    selector: 'topic-item-condensed',
    templateUrl: './topic-item-condensed.html',
    styleUrls: ['./topic-item-condensed.scss'],
    encapsulation: ViewEncapsulation.None,
    // changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        'class': 'st-topic-item-condensed'
    }
})
export class TopicItemCondensed extends DraggableEnpsComponent implements AfterViewInit {
    public readonly FieldDefValidations = FieldDefValidations;
    public toggleApprovedValue: boolean = this.data?.approved;
    recordChangedSubscription: Subscription = null;
    topicEditable: boolean = true;
    assignmentsReadable: boolean = true;
    isPanelOpen: boolean = false;
    pstFieldNames = PSTFieldNames;
    toolTipData: Array<string> = [];
    isListOpen: boolean = false;
    isAssignmentsPanelOpen = false;

    @ViewChild(MatButton) btnApproved: MatButton;
    @ViewChild('externalActions', { read: TemplateRef }) externalActions: TemplateRef<any>;
    @ViewChild('assignmentsOverlay', { read: TemplateRef }) assignmentsOverlay: TemplateRef<any>;
    @ViewChild('assignmentsOverlayEl', { read: CdkConnectedOverlay }) assignmentsOverlayEl: CdkConnectedOverlay;
    @ViewChild('externalActionsEl', { read: CdkConnectedOverlay }) externalActionsEl: CdkConnectedOverlay;
    @ViewChild('storyOwnerEl', { read: CdkConnectedOverlay }) storyOwnerEl: CdkConnectedOverlay;
    @Output() public onTopicOpen: EventEmitter<TopicRecord> = new EventEmitter();
    @Output() public onAssignmentOpen: EventEmitter<AssignmentRecord> = new EventEmitter();

    private _data: TopicRecord = null;
    @Input()
    public get data(): TopicRecord {
        return this._data;
    }
    public set data(v: TopicRecord) {
        this._data = v;
        if (v) {
            this._guid = v?.guid;
            this.topicEditable = this._data?.getSecurityPrivsWrite(this.appService);
            this.assignmentsReadable = this.data?.getSecurityPrivsReadChildren(this.appService);
            if (this.data && this.data?.path && this.data?.path.isWasteBinPath) {
                this.topicEditable = false;
                this.isReadOnly = true;
            }
            if (!this.recordChangedSubscription) this.recordChangedSubscription = super.createSubscription(this._data?.changeSubject, this.recordChanged.bind(this))
        }
    }

    private _isReadonly: boolean = false;
    @Input()
    public get isReadOnly(): boolean {
        return this._isReadonly || this.viewStateService.inSlideshowMode;
    }
    public set isReadOnly(v: boolean) {
        if (!v && this.topicEditable) {
            this._isReadonly = v;
        } else if (v) {
            this._isReadonly = v;
        }
    }

    private _isPublication: boolean = false;
    @Input()
    public get isPublication(): boolean {
        return this._isPublication;
    }
    public set isPublication(v: boolean) {
        this._isPublication = v;
    }
    @Output() public onDeletePublication: EventEmitter<any> = new EventEmitter();

    public get viewStateCustomCondensedItems(): Array<FieldDef> {
        if (this.viewStateService.ViewStateRecord instanceof TopicViewStateRecord || this.viewStateService.ViewStateRecord instanceof SingleFeedViewStateRecord || this.viewStateService.ViewStateRecord instanceof PublicationViewStateRecord) {
            return this.viewStateService.ViewStateRecord.viewStateCustomCardItems;
        }
        return [];
    }

    constructor(
        public appService: AppService,
        public playbookService: PlaybookService,
        timeoutQueue: TimeoutQueue,
        public viewStateService: LayoutState,
        public baseService: BasicContentService,
        public globalTablesService: GlobalTablesService,
        public visualStateService: VisualStateService,
        private vref: ViewContainerRef,
        public languageSettings: LanguageSettings

    ) {
        super(timeoutQueue);
    }

    get title(): string {
        return this.data?.title;
    }

    get tags(): string {
        let tagsList = [];
        this.data.tags.map((tags) => {
            tagsList.push(tags.displayText)
        });
        return tagsList.join(', ');
    }

    get description(): string {
        return this.data?.summary ? this.data.summary : this.globalTablesService.FieldDefsDict[this.pstFieldNames.PSTTopicSummary]?.DefaultCaption;
    }

    get status(): string {
        return this.data?.status;
    }

    get topicGroupsItem(): string {
        return this.data?.department?.join(', ');
    }

    get extraOwnerCount(): number {
        return this.data?.storyOwner?.length - 1;
    }

    get shouldShowCustomFields(): boolean {
        return !this.visualStateService.isLessThanMedium;
    }

    get allUsers(): Staff[] {
        return Object.values(this.globalTablesService.Staff);
    }


    get tooltipUsers(): Staff[] {
        if (this.toolTipData?.length > 5) {
            let users = this.data.storyOwner?.slice(4)
            return this.allUsers.filter(option => (users.includes(option.UserID))).map((item) => item);
        } else {
            return this.allUsers.filter(option => (this.toolTipData.includes(option.UserID))).map((item) => item);
        }
    }

    get shouldShowExtraAssignments(): boolean {
        return this.data?.children?.length > 4;
    }

    get extraAssignmentsCount(): number {
        return this.data?.children?.length - 3;
    }

    @HostListener('click', ['$event'])
    public onClick(event: any) {
        if (this.isReadOnly) {
            return;
        }
        this.onTopicOpen.emit(this.data);
    }

    async ngAfterViewInit() {
        setTimeout(() => {
            if (this.externalActions) {
                this.vref.createEmbeddedView(this.externalActions);
                this.vref.createEmbeddedView(this.assignmentsOverlay);
            }
        }, 0)
    }

    updateTopic(value: any, fieldId: string) {
        EnpsRecordControl.saveChanges(value, fieldId, this.data);
    }

    public getCondensedItemLabel(item: any) {
        if (!item) {
            return "";
        }
        const itemLabel = 'PSTWebListValue'
        return this.appService.languageSettings.get(`${itemLabel}_${(item.id) ? item.title : item}`).includes(itemLabel) ? ((item.id) ? item.title : item) : this.appService.languageSettings.get(`${itemLabel}_${(item.id) ? item.title : item}`);
    }

    recordChanged() {
        this.topicEditable = this.data?.getSecurityPrivsWrite(this.appService);
        this.assignmentsReadable = this.data?.getSecurityPrivsReadChildren(this.appService);
        if (this.data?.path?.isWasteBinPath) {
            this.topicEditable = false;
            this.isReadOnly = true;
        }
        this.itemStringCache.clear();
    }

    getInnerProp(item: FieldDef) {
        const val = this.data?.get(item.ID.split('-')[0]);
        if (val) return val[item.ComplexPropInnerId]; else return '';
    }

    public itemStringCache = new Map<string, string>();
    getItemString(item: FieldDef): string {
        if (this.itemStringCache.has(item?.ID)) {
            return this.itemStringCache.get(item?.ID);
        }
        let str = "";
        const itemData = this.data.get(item?.ID, null);
        switch (item?.Validation?.toUpperCase()) {
            case FieldDefValidations.CHECKBOX: {
                str = itemData ? this.appService.languageSettings.get('PSTWebYesOption') : this.appService.languageSettings.get('PSTWebNoOption');
                break;
            }
            case FieldDefValidations.CHECKLISTEDIT:
            case FieldDefValidations.CHECKLIST: {
                if (itemData) {
                    const options = this.globalTablesService.getListItemsForField(item?.ID);
                    str = (itemData as MultiValue<String>).map((item) => {
                        return options.find((option) => option.id === item)?.title;
                    }).join(', ');
                }
                break;
            }
            case FieldDefValidations.STAFFIDLIST: {
                if (itemData) {
                    str = (itemData as MultiValue<string>).map((item) => {
                        return this.globalTablesService.Staff[item]?.DisplayName;
                    }).join(', ');
                }
                break;
            }
            case FieldDefValidations.COMBOBOX: {
                if (itemData) {
                    const options = this.globalTablesService.getListItemsForField(item?.ID);
                    str = options.find((option) => option.id === itemData)?.title;
                }
                break;
            }
            case FieldDefValidations.TIME: {
                if (itemData) {
                    str = (itemData as DateTime)?.toDateTimeString();
                }
                break;
            }
            case FieldDefValidations.DATE: {
                if (itemData) {
                    str = (itemData as DateTime)?.toDateString();
                }
                break;
            }
            case FieldDefValidations.ADDRESS: {
                if (itemData) {
                    if (item.ComplexPropInnerId) {
                        str = this.getInnerProp(item);
                    } else {
                        str = itemData?.toString();
                    }
                }
                break;
            }
            default: {
                str = itemData?.toString() || '';
                break;
            }
        }
        this.itemStringCache.set(item?.ID, str);
        return str;
    }

    onMouseOutAssignments(event: MouseEvent) {
        if (!(event.relatedTarget as HTMLElement)?.classList?.contains('st-assignments-overlay')) {
            this.isAssignmentsPanelOpen = false;
        }
    }

    assignmentsPanelAttached() {
        this.panelAttached(this.assignmentsOverlayEl);
    }

    storyOwnerPanelAttached() {
        this.panelAttached(this.storyOwnerEl);
    }

    private panelAttached(overlayEl: CdkConnectedOverlay = null) {
        const overlayElement = overlayEl?.overlayRef?.overlayElement;
        if (overlayElement) {
            overlayElement.addEventListener("mouseleave", (e) => {
                this.isPanelOpen = false;
                this.isAssignmentsPanelOpen = false;
                this.isListOpen = false;
            });
        }
    }

    showStaffTooltip(value) {
        this.toolTipData = [...value]
        this.isListOpen = true;
    }


    onStoryOwnerMouseOut(event: MouseEvent) {
        if (!(event.relatedTarget as HTMLElement)?.classList?.contains('st-staff-id-list-tooltip-content')) {
            this.isListOpen = false;
        }
    }

    public deletePublication(event: any) {
        event.stopPropagation();
        this.onDeletePublication.emit();
    }


    focusDuplicate() {
        this.onTopicOpen.emit();
    }
}
